'use client';

// The signed-in visitor's saved rooms, newest first: one /api/scenes read per
// page load, shared by everything on a public page that offers to open one
// (the homepage's "Continue", a gear list's "Use in my room"). Public pages
// are static, so like /api/auth/me (sessionSummary.ts) this is the client's
// question to ask.

import { useEffect, useState } from 'react';

export interface SavedRoom {
  token: string;
  title: string;
}

let roomsPromise: Promise<SavedRoom[]> | null = null;

export function fetchSavedRooms(): Promise<SavedRoom[]> {
  if (!roomsPromise) {
    roomsPromise = fetch('/api/scenes', { credentials: 'include', cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { scenes?: { token: string; title: string | null }[] } | null) =>
        (data?.scenes ?? []).map((s) => ({ token: s.token, title: s.title || 'Untitled room' })))
      .catch(() => []);
  }
  return roomsPromise;
}

/** Test seam: forget the memoised read. */
export function resetSavedRoomsCache(): void {
  roomsPromise = null;
}

/** The visitor's rooms once known; null while signed out or still asking. */
export function useSavedRooms(signedIn: boolean): SavedRoom[] | null {
  const [rooms, setRooms] = useState<SavedRoom[] | null>(null);
  useEffect(() => {
    if (!signedIn) return;
    let cancelled = false;
    void fetchSavedRooms().then((list) => { if (!cancelled) setRooms(list); });
    return () => { cancelled = true; };
  }, [signedIn]);
  return signedIn ? rooms : null;
}
